import { type GetBalanceCurrencyDTO } from '@capsicum/common';
import { useCallback, useMemo } from 'react';

import { useWalletsBalanceQuery } from '../data-access/queries/useWalletsBalanceQuery';
import { hasEnoughToken } from '../models/util/walletHasEnoughToken';
import {
	excludedCurrenciesShortcodes,
	type UseWalletsBalanceProps,
	type UseWalletsBalanceType
} from '../models/walletBalance';

/**
 *
 * Contains balance information of BOTH custodial and nonCustodial Wallets.
 */
export const useWalletsBalance = ({
	isStakable = false,
	removeExcludedCurrencies = false
}: UseWalletsBalanceProps = {}): UseWalletsBalanceType => {
	const { data: walletsBalance, isLoading } = useWalletsBalanceQuery({ isStakable });

	//TODO: to remove. Currently walletsBalance.currencies is a Object, not a Array; awaiting BE to fix currencies data type
	//TODO: remove also from useNcwBalance hook.
	const currencies: Array<GetBalanceCurrencyDTO> = useMemo(() => {
		const allCurrencies: Array<GetBalanceCurrencyDTO> = !walletsBalance?.currencies
			? []
			: Array.isArray(walletsBalance.currencies)
				? walletsBalance.currencies
				: typeof walletsBalance.currencies === 'object'
					? Object.values(walletsBalance.currencies)
					: [];

		return removeExcludedCurrencies
			? allCurrencies.filter(c => !excludedCurrenciesShortcodes.includes(c.currency.shortCode))
			: allCurrencies;
	}, [walletsBalance, removeExcludedCurrencies]);

	const totalFanTokensBalance = useMemo(() => {
		const currenciesWithoutExcluded = currencies.filter(
			c => !excludedCurrenciesShortcodes.includes(c.currency.shortCode)
		);

		const balance = currenciesWithoutExcluded.reduce((acc, curr) => {
			return Number(acc) + Number(curr.totalAmount);
		}, 0);

		return balance || 0;
	}, [currencies]);

	const getCurrencyBy = useCallback(
		(predicate: (b: GetBalanceCurrencyDTO) => boolean) => currencies?.find(predicate),
		[currencies]
	);

	const hasEnoughBalance = useCallback(
		(currencyId: string, amountRequired: number): boolean => {
			const currency = getCurrencyBy(c => c.currency.id === currencyId);
			const checkTokenAmount = hasEnoughToken(Number(currency?.totalAmount) || 0);

			return checkTokenAmount(amountRequired);
		},
		[getCurrencyBy]
	);

	const hasEnoughStaked = useCallback(
		(currencyId: string, amountRequired: number): boolean => {
			const currency = getCurrencyBy(c => c.currency.id === currencyId);
			const checkTokenAmount = hasEnoughToken(Number(currency?.stakedAmount) || 0);

			return checkTokenAmount(amountRequired);
		},
		[getCurrencyBy]
	);

	return useMemo(
		() => ({
			currencies,
			getCurrencyBy,
			hasEnoughBalance,
			hasEnoughStaked,
			isLoading,
			totalFanTokensBalance,
			walletsBalance
		}),
		[hasEnoughBalance, isLoading, currencies, walletsBalance, totalFanTokensBalance, getCurrencyBy]
	);
};
